import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ChatContext = createContext(null);
let audioCtx = null;

function unlockAudio() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const b = audioCtx.createBuffer(1, 1, 22050);
    const src = audioCtx.createBufferSource();
    src.buffer = b;
    src.connect(audioCtx.destination);
    src.start(0);
  } catch {
    /* audio not available */
  }
}
["pointerdown", "keydown", "touchstart"].forEach((ev) => window.addEventListener(ev, unlockAudio, { passive: true }));

function playChime() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    [880, 1320, 880, 1320].forEach((freq, i) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      const t = audioCtx.currentTime + i * 0.16 + (i > 1 ? 0.25 : 0);
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.5, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.connect(g).connect(audioCtx.destination);
      o.start(t);
      o.stop(t + 0.32);
    });
    navigator.vibrate?.(150);
  } catch {
    /* audio not available */
  }
}

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [lastSeen, setLastSeen] = useState("");
  const [soundOn, setSoundOnState] = useState(() => localStorage.getItem("chat_sound") !== "off");
  const lastTs = useRef("");
  const deletedSince = useRef("1970-01-01");
  const activeRef = useRef(false);
  const soundRef = useRef(soundOn);
  const seenKey = user ? `chat_seen_${user.id}` : "";

  const setSoundOn = (v) => { setSoundOnState(v); soundRef.current = v; localStorage.setItem("chat_sound", v ? "on" : "off"); if (v) playChime(); };

  const markSeen = useCallback(() => {
    if (!seenKey || !lastTs.current) return;
    localStorage.setItem(seenKey, lastTs.current);
    setLastSeen(lastTs.current);
  }, [seenKey]);

  const append = useCallback((list, fromServerPoll = true) => {
    if (!list.length) return;
    if (fromServerPoll) {
      const latest = list.reduce((mx, x) => (x.created_at > mx ? x.created_at : mx), lastTs.current);
      lastTs.current = latest;
    }
    setMessages((m) => {
      const ids = new Set(m.map((x) => x.id));
      return [...m, ...list.filter((x) => !ids.has(x.id))].sort((a, b) => a.created_at.localeCompare(b.created_at));
    });
    if (activeRef.current) markSeen();
  }, [markSeen]);

  useEffect(() => {
    if (!user) {
      setMessages([]);
      lastTs.current = "";
      return undefined;
    }
    setLastSeen(localStorage.getItem(seenKey) || "");
    let stop = false;
    api.get("/chat/messages").then(({ data }) => {
      if (stop) return;
      if (!localStorage.getItem(seenKey) && data.length) {
        localStorage.setItem(seenKey, data[data.length - 1].created_at);
        setLastSeen(data[data.length - 1].created_at);
      }
      append(data);
    }).catch(() => {});
    const t = setInterval(async () => {
      if (!lastTs.current) {
        const { data } = await api.get("/chat/messages").catch(() => ({ data: [] }));
        append(data);
        return;
      }
      try {
        const { data } = await api.get("/chat/messages", { params: { after: lastTs.current } });
        if (data.some((m) => m.sender_id !== user.id) && soundRef.current) playChime();
        append(data);
        const { data: del } = await api.get("/chat/deleted", { params: { since: deletedSince.current } });
        deletedSince.current = del.now;
        if (del.ids.length) {
          const gone = new Set(del.ids);
          setMessages((m) => m.filter((x) => !gone.has(x.id)));
        }
      } catch {
        /* retry next tick */
      }
    }, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [user, seenKey, append]);

  const send = async (payload) => {
    const { data } = await api.post("/chat/messages", payload);
    append([data], false);
  };

  const remove = async (id) => {
    await api.delete(`/chat/messages/${id}`);
    setMessages((m) => m.filter((x) => x.id !== id));
  };

  const setActive = (v) => { activeRef.current = v; if (v) markSeen(); };
  const unread = user ? messages.filter((m) => m.sender_id !== user.id && m.created_at > lastSeen).length : 0;

  return <ChatContext.Provider value={{ messages, send, remove, unread, setActive, soundOn, setSoundOn }}>{children}</ChatContext.Provider>;
}

export const useChat = () => useContext(ChatContext);
